import userService from "~/composables/services/user";
import ENDPOINTS from "~/composables/config/routes";

function checkEnvironmentVariables(url: any, route: any): boolean {
  if (!url || !route) {
    useNotificationStore().error({
      variant: "standardAlert",
      messageHead: "Oops",
      messageBody:
        "API routes not found. Environment variables might be missing."
    });
    return false;
  }
  return true;
}

export const actions = {
  async signup(
    name: string,
    surname: string,
    email: string,
    password: string,
    passwordConfirmation: string,
    countryId: string,
    countryStateId: string,
    leadTeam: string,
    termsAcceptance: boolean,
    company: string,
    job: string
  ) {
    const store = useUserStore();
    const config = useRuntimeConfig();

    if (config.public.API_URL && config.public.USER_ROUTE) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.SIGNUP_ROUTE}`;

      try {
        const user = await userService.signup(
          url,
          name,
          surname,
          email,
          password,
          passwordConfirmation,
          Number(countryId),
          Number(countryStateId),
          Boolean(leadTeam),
          termsAcceptance,
          company,
          job
        );
        
        if (process.env.NODE_ENV === 'production' && user?.id) {
          if (typeof window !== 'undefined' && window.$bugsnag) {
            window.$bugsnag.user = {
              id: String(user.id),
              name: `${user.name || ''} ${user.surname || ''}`,
              email: user.email || '',
            };
          }
        }
        
        await useRouter().push("/login");
        store.$patch({
          user: user,
          status: {
            loggedIn: !!user.email,
            activationPending: true,
            errorMessage: undefined
          },
          failedRequests: 0
        });

        return { error: false, message: "" };
      } catch (error) {
        console.log("error", error);

        // The service throws JSON-encoded { error, message } for signup
        // failures, but a raw Error can still escape (e.g. logout cleanup or a
        // malformed response). Fall back to a generic message in that case so
        // the store state is always updated consistently.
        let errorMessage: { error: boolean; message: string };
        try {
          errorMessage = JSON.parse((error as Error).message);
        } catch {
          errorMessage = {
            error: true,
            message: "Signup failed due to an unexpected error"
          };
        }

        store.$patch({
          status: {
            loggedIn: false,
            activationPending: false,
            errorMessage: {
              error: errorMessage.error,
              message: errorMessage.message
            }
          },
          failedRequests: (store.failedRequests || 0) + 1
        });

        return { error: true, message: errorMessage.message };
      }
    } else {
      useNotificationStore().error({
        variant: "standardAlert",
        messageHead: "Oops",
        messageBody:
          "API routes not found. Environment variables might be missing."
      });

      return {
        error: true,
        message: "API routes not found. Environment variables might be missing."
      };
    }
  },

  async fireError(payload: any) {
    const store = useUserStore();
    store.error = payload;
  },

  async update(
    name: string,
    surname: string,
    email: string,
    countryId: string,
    countryStateId: string,
    leadTeam: number,
    loginUsername: string,
    loginPassword: string,
    password: string,
    passwordConfirmation: string
  ): Promise<{ success: boolean; message: string }> {
    const store = useUserStore();
    const config = useRuntimeConfig();
    const isLeadTeam = leadTeam;

    if (config.public.API_URL && config.public.USER_ROUTE) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.ACCOUNT_UPDATE_ROUTE}`;

      store.$patch({
        status: {
          updatedProfile: true
        }
      });

      try {
        const data = await userService.update(
          url,
          name,
          surname,
          email,
          Number(countryId),
          Number(countryStateId),
          isLeadTeam,
          loginUsername,
          loginPassword,
          password,
          passwordConfirmation
        );

        store.$patch({
          user: data.user
        });

        await useRouter().push("/my-account/profile");

        return {
          success: true,
          message: "Profile updated successfully."
        };
      } catch (error) {
        const errMsg = (error as Error).message || "Unknown error";

        useNotificationStore().error({
          variant: "error",
          messageHead: "[update] API Error:",
          messageBody: errMsg
        });

        store.$patch({
          status: {
            updatedProfile: false,
            errorMessage: {
              error: true,
              message: errMsg
            }
          },
          failedRequests: (store.failedRequests || 0) + 1
        });

        return {
          success: false,
          message: errMsg
        };
      }
    } else {
      const configError =
        "API config missing: " +
        JSON.stringify({
          API_URL: config.public.API_URL,
          USER_ROUTE: config.public.USER_ROUTE
        });

      useNotificationStore().error({
        variant: "error",
        messageHead: "[update] Missing API config:",
        messageBody: configError
      });

      useNotificationStore().error({
        variant: "standardAlert",
        messageHead: "Oops",
        messageBody:
          "API routes not found. Environment variables might be missing."
      });

      return {
        success: false,
        message: "API config is missing. Cannot update profile."
      };
    }
  },

  async activate(code: string) {
    const store = useUserStore();
    const config = useRuntimeConfig();

    if (config.public.API_URL && config.public.USER_ROUTE) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.ACTIVATION_ROUTE}`;

      try {
        const user = await userService.activate(url, code);
        
        if (process.env.NODE_ENV === 'production' && user?.id) {
          if (typeof window !== 'undefined' && window.$bugsnag) {
            window.$bugsnag.user = {
              id: String(user.id),
              name: `${user.name || ''} ${user.surname || ''}`,
              email: user.email || '',
            };
          }
        }
        
        store.$patch({
          user: user,
          status: {
            loggedIn: true,
            errorMessage: undefined
          },
          failedRequests: 0
        });
        await useRouter().push("/login");
      } catch (error) {
        const errorMessage = JSON.parse((error as Error).message);
        store.$patch({
          status: {
            loggedIn: false,
            errorMessage: {
              error: true,
              message: errorMessage?.message
            }
          },
          failedRequests: (store.failedRequests || 0) + 1
        });
      }
    } else {
      useNotificationStore().error({
        variant: "standardAlert",
        messageHead: "Oops",
        messageBody:
          "API routes not found. Environment variables might be missing."
      });
    }
  },

  async logout() {
    const store = useUserStore();
    const config = useRuntimeConfig();
    const notification = useNotificationStore();

    const apiUrl = config.public.API_URL;
    const userRoute = config.public.USER_ROUTE;

    if (checkEnvironmentVariables(apiUrl, userRoute)) {
      const url = `${apiUrl}${userRoute}${ENDPOINTS.LOGOUT_ROUTE}`;

      try {
        await userService.logout(url);
        
        if (process.env.NODE_ENV === 'production') {
          if (typeof window !== 'undefined' && window.$bugsnag) {
            window.$bugsnag.user = {};
          }
        }
        
        store.$reset();
        await useRouter().push("/login");
      } catch (error) {
        store.failedRequests++;
        notification.error({
          variant: "error",
          messageHead: "Logout failed",
          messageBody: String(error)
        });
      }
    } else {
      notification.error({
        variant: "error",
        messageHead: "Missing config",
        messageBody: "API_URL or USER_ROUTE is not set."
      });
    }
  },

  async login(email: string, password: string, recaptcha: string) {
    const store = useUserStore();
    const config = useRuntimeConfig();
    if (
      checkEnvironmentVariables(config.public.API_URL, config.public.USER_ROUTE)
    ) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.LOGIN_ROUTE}`;
      try {
        const user = await userService.login(url, email, password, recaptcha);

        if (process.env.NODE_ENV === 'production' && user?.id) { 
          if (typeof window !== 'undefined' && window.$bugsnag) {
            window.$bugsnag.user = {
              id: String(user.id),
              name: `${user.name || ''} ${user.surname || ''}`,
              email: user.email || '',
            };
          }
        }

        store.$patch({
          user: user,
          status: {
            loggedIn: true,
            failedAttempt: false
          },
          password: {
            resetRequested: false,
            resetConfirmed: false
          },
          failedRequests: 0,
          isAuthenticated: true,
          permissionGroups: user.groups
        });

        if (user.has_temporary_password && user.resetData) {
          const code = user.resetData.code;
          await useRouter().push(`/reset-password/${code}`);
        } else {
          await useRouter().push("/");
        }
      } catch (error) {
        const err = error as Error;
        store.$patch({
          user: {},
          status: {
            loggedIn: false,
            failedAttempt: true,
            errorMessage: {
              error: true,
              message: err.message || "Login failed"
            }
          },
          permissionGroups: []
        });

        useNotificationStore().error({
          variant: "standardAlert",
          messageHead: "Oops",
          messageBody: "Login failed:" + error
        });
      }
    }
  },

  async forgotpassword(email: string, recaptcha: string) {
    const store = useUserStore();
    const config = useRuntimeConfig();
    if (
      checkEnvironmentVariables(config.public.API_URL, config.public.USER_ROUTE)
    ) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.FORGOT_PASSWORD}`;
      try {
        await userService.forgotpassword(url, email, recaptcha);
        store.password = {
          resetRequested: true,
          resetConfirmed: false
        };
      } catch (error) {
        const err = error as Error;
        store.$patch({
          user: {},
          status: {
            loggedIn: false,
            failedAttempt: true,
            errorMessage: {
              error: true,
              message: err.message || "Login failed"
            }
          },
          permissionGroups: [],
          failedRequests: (store.failedRequests || 0) + 1
        });
        useNotificationStore().error({
          variant: "error",
          messageHead: "Password reset request failed:",
          messageBody: String(error)
        });
      }
    }
  },

  async resetPassword(
    code: string,
    password: string,
    passwordConfirmation: string,
    recaptcha: string
  ) {
    const store = useUserStore();
    const config = useRuntimeConfig();
    if (
      checkEnvironmentVariables(config.public.API_URL, config.public.USER_ROUTE)
    ) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.RESET_PASSWORD}`;
      try {
        await userService.resetpassword(
          url,
          code,
          password,
          passwordConfirmation,
          recaptcha
        );
        store.password = {
          resetRequested: false,
          resetConfirmed: true
        };
        // The backend clears has_temporary_password on a successful reset;
        // mirror it locally so the forced-reset route exemption stops applying.
        if (store.user?.has_temporary_password) {
          store.user = {
            ...store.user,
            has_temporary_password: false,
            resetData: undefined
          };
        }
      } catch (error) {
        const err = error as Error;
        store.$patch({
          user: {},
          status: {
            loggedIn: false,
            failedAttempt: true,
            errorMessage: {
              error: true,
              message: err.message || "Login failed"
            }
          },
          permissionGroups: [],
          failedRequests: (store.failedRequests || 0) + 1
        });
      }
    }
  },

  async checkToken() {
    const config = useRuntimeConfig();
    if (config.public.API_URL && config.public.USER_ROUTE) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.CHECK_ROUTE}`;
      try {
        const user = await userService.checkToken(url);
        return user;
      } catch (error) {
        await this.logout();
        const errorMessage =
          (error as Error).message || "An unknown error occurred";
        throw new Error(errorMessage);
      }
    } else {
      useNotificationStore().error({
        variant: "standardAlert",
        messageHead: "Oops",
        messageBody:
          "API routes not found. Environment variables might be missing."
      });
    }
  },

  async clearPasswordResetStatus() {
    const store = useUserStore();
    store.password = {
      resetRequested: false,
      resetConfirmed: false
    };
  },

  resetFailedRequestCount() {
    const store = useUserStore();
    store.failedRequests = 0;
  },

  updateUserPlans({ userPlans }: { userPlans: any }) {
    const store = useUserStore();
    store.userPlans = userPlans;
  },

  async updateFailedRequestCount() {
    const store = useUserStore();
    store.failedRequests = (store.failedRequests || 0) + 1;

    const status = store.failedRequests;
    if (typeof status === "number") {
      if (status >= 20) {
        await this.logout();
        useNotificationStore().error({
          variant: "persistentAlert",
          messageHead:
            "Apologies, you have been logged out due to an authentication error.",
          messageBody:
            "Please try logging in again or get in touch to find out more."
        });
      } else if (status === 10) {
        useNotificationStore().error({
          variant: "standardAlert",
          messageHead:
            "Oops, it looks like something might be wrong with the connection to our servers...",
          messageBody:
            "Please try refreshing the page or try again later if the problem persists."
        });
      }
    }
  },

  async validateCurrentPassword(
    email: string,
    password: string
  ): Promise<boolean> {
    const config = useRuntimeConfig();
    const notificationStore = useNotificationStore();

    if (
      checkEnvironmentVariables(config.public.API_URL, config.public.USER_ROUTE)
    ) {
      const url = `${config.public.API_URL}${config.public.USER_ROUTE}${ENDPOINTS.LOGIN_ROUTE}`;
      try {
        await userService.login(url, email, password, "");
        return true;
      } catch {
        notificationStore.error({
          variant: "standardAlert",
          messageHead: "Oops",
          messageBody: "Current password is incorrect."
        });
        return false;
      }
    }
    return false;
  }
};
